import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { config } from '../../config';
import { sanitizeCourseHtml } from '../../services/courseHtml';
import '../../components/common/course-editor.css';
import './lecteur-cours.css';

function prepareCourseDocument(html) {
  const doc = new DOMParser().parseFromString(sanitizeCourseHtml(html || '<p>Aucun contenu pour cette page.</p>'), 'text/html');
  const headings = [];
  const anchors = new Map();
  const usedIds = new Set(Array.from(doc.querySelectorAll('[id]'), node => node.id));
  doc.querySelectorAll('h1, h2, h3, h4').forEach((node, index) => {
    const label = (node.textContent || '').replace(/\s+/g, ' ').trim();
    if (!label) return;
    const previousId = node.id;
    let id = 'course-reader-section-' + (index + 1);
    while (usedIds.has(id)) id += '-';
    usedIds.add(id);
    node.id = id;
    node.tabIndex = -1;
    node.setAttribute('data-reader-heading', 'true');
    if (previousId && !anchors.has(previousId)) anchors.set(previousId, id);
    headings.push({ id, label, level: Number(node.tagName.slice(1)) });
  });
  doc.querySelectorAll('a[href^="#"]').forEach(link => {
    let original = link.getAttribute('href').slice(1);
    try { original = decodeURIComponent(original); } catch (_) { /* Ancre littérale. */ }
    if (anchors.has(original)) link.setAttribute('href', '#' + anchors.get(original));
  });
  doc.querySelectorAll('table').forEach(table => {
    if (table.parentElement?.classList.contains('tableWrapper')) {
      table.parentElement.classList.add('course-reader-table');
    } else {
      const wrapper = doc.createElement('div');
      wrapper.className = 'course-reader-table';
      table.parentNode.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  });
  return { html: doc.body.innerHTML, headings };
}

function scrollReaderToStart(root) {
  if (!root) return;
  const offset = window.innerWidth < 1024 ? (document.querySelector('.student-mobile-header')?.getBoundingClientRect().height || 0) : 0;
  window.scrollTo({ top: Math.max(0, root.getBoundingClientRect().top + window.scrollY - offset), behavior: 'auto' });
}

export default function LecteurCours() {
  const { idCours } = useParams();
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(() => window.matchMedia('(min-width: 1280px)').matches);
  const [compact, setCompact] = useState(() => window.matchMedia('(max-width: 1279px)').matches);
  const [wideReading, setWideReading] = useState(false);
  const [tocSearch, setTocSearch] = useState('');
  const [activeHeading, setActiveHeading] = useState('');
  const readerRef = useRef(null), toolbarRef = useRef(null), contentRef = useRef(null);
  const titleRef = useRef(null), tocRef = useRef(null), drawerRef = useRef(null), closeRef = useRef(null);
  const documentContent = useMemo(() => prepareCourseDocument(currentPage?.contenu), [currentPage?.contenu]);
  const currentIndex = pages.findIndex(page => page.id_page === currentPage?.id_page);
  const search = tocSearch.trim().toLocaleLowerCase('fr');
  const visiblePages = pages.filter(page => String(page.titre || '').toLocaleLowerCase('fr').includes(search) || (page.id_page === currentPage?.id_page && documentContent.headings.some(heading => heading.label.toLocaleLowerCase('fr').includes(search))));

  useEffect(() => {
    const media = window.matchMedia('(max-width: 1279px)');
    const change = () => { setCompact(media.matches); setSidebarOpen(!media.matches); };
    media.addEventListener('change', change);
    return () => media.removeEventListener('change', change);
  }, []);

  useEffect(() => {
    const shell = readerRef.current?.closest('.student-shell');
    if (!shell || !wideReading) return undefined;
    shell.classList.add('student-shell-reader-wide');
    const key = event => { if (event.key === 'Escape') setWideReading(false); };
    window.addEventListener('keydown', key);
    return () => { shell.classList.remove('student-shell-reader-wide'); window.removeEventListener('keydown', key); };
  }, [wideReading, loading]);

  useEffect(() => {
    if (!compact || !sidebarOpen) return undefined;
    const previousFocus = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const key = event => {
      if (event.key === 'Escape') { event.stopImmediatePropagation(); setSidebarOpen(false); }
      if (event.key !== 'Tab') return;
      const elements = drawerRef.current?.querySelectorAll('button:not([disabled]), input, a[href]');
      if (!elements?.length) return;
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', key, true);
    return () => {
      document.body.style.overflow = overflow;
      window.removeEventListener('keydown', key, true);
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [compact, sidebarOpen]);

  useEffect(() => {
    if (!currentPage?.id_page || loading) return undefined;
    setActiveHeading('');
    const frame = requestAnimationFrame(() => {
      const root = readerRef.current;
      if (!root) return;
      scrollReaderToStart(root);
      titleRef.current?.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [currentPage?.id_page, loading]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return undefined;
    const headings = Array.from(content.querySelectorAll('[data-reader-heading]'));
    let frame = null;
    const update = () => {
      frame = null;
      const threshold = Math.max(128, (toolbarRef.current?.getBoundingClientRect().bottom || 0) + 24);
      let active = headings[0]?.id || '';
      for (const heading of headings) {
        if (heading.getBoundingClientRect().top <= threshold) active = heading.id;
        else break;
      }
      setActiveHeading(active);
    };
    const schedule = () => { if (frame === null) frame = requestAnimationFrame(update); };
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(schedule) : null;
    observer?.observe(content);
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    content.addEventListener('load', schedule, true);
    schedule();
    return () => {
      if (frame !== null) cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      content.removeEventListener('load', schedule, true);
    };
  }, [documentContent.html, currentPage?.id_page, loading]);

  useEffect(() => {
    const nav = tocRef.current;
    if (!nav) return;
    const target = activeHeading ? nav.querySelector('[data-section-target="' + activeHeading + '"]') : nav.querySelector('[aria-current="page"]');
    if (!target) return;
    const bounds = nav.getBoundingClientRect(), item = target.getBoundingClientRect();
    if (item.top < bounds.top + 12) nav.scrollTop += item.top - bounds.top - 12;
    else if (item.bottom > bounds.bottom - 12) nav.scrollTop += item.bottom - bounds.bottom + 12;
  }, [activeHeading, sidebarOpen, currentPage?.id_page]);

  const selectPage = page => {
    setCurrentPage(page); setTocSearch('');
    if (compact) setSidebarOpen(false);
    if (page.id_page === currentPage?.id_page) scrollReaderToStart(readerRef.current);
  };

  const goToHeading = id => {
    const node = Array.from(contentRef.current?.querySelectorAll('[data-reader-heading]') || []).find(heading => heading.id === id);
    if (!node) return;
    if (compact) setSidebarOpen(false);
    requestAnimationFrame(() => {
      node.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
      node.focus({ preventScroll: true }); setActiveHeading(id);
    });
  };

  const API_BASE = config.apiBase;
  const getToken = () => localStorage.getItem('token');

  // ✅ Normalisation des pages avec médias
  const normalizePages = (pagesData) => {
    if (!pagesData) return [];
    return pagesData.map(page => ({
      ...page,
      medias: (page.medias || []).map(media => {
        let typeMedia = media.type_media;
        let url = media.url_fichier;
        
        // ✅ Détection automatique YouTube
        if (url && (url.includes('youtube.com') || url.includes('youtu.be'))) {
          typeMedia = 'youtube';
        }
        
        return {
          ...media,
          type_media: typeMedia,
          url_fichier: config.normalizeCourseMedia ? config.normalizeCourseMedia(media.url_fichier) : media.url_fichier
        };
      })
    }));
  };

  // ✅ Fonction pour extraire l'ID YouTube
  const extractYouTubeId = (url) => {
    if (!url) return null;
    const patterns = [
      /(?:youtube\.com\/watch\?v=)([^&]+)/,
      /(?:youtu\.be\/)([^?]+)/,
      /(?:youtube\.com\/embed\/)([^?]+)/,
      /(?:youtube\.com\/v\/)([^?]+)/
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    if (/^[a-zA-Z0-9_-]{11}$/.test(url)) {
      return url;
    }
    return null;
  };

  // ✅ Vérifier si une URL est une vidéo YouTube
  const isYouTubeUrl = (url) => {
    if (!url) return false;
    return url.includes('youtube.com') || url.includes('youtu.be');
  };

  useEffect(() => {
    const controller = new AbortController();
    let alive = true;
    setLoading(true); setPages([]); setCurrentPage(null);
    const fetchPages = async () => {
      try {
        const token = getToken();
        if (!token) {
          toast.error('Veuillez vous reconnecter');
          navigate('/login');
          return;
        }

        const url = `${API_BASE}/etudiant/cours/get_contenu_page.php?id_cours=${idCours}`;
        const response = await fetch(url, {
          headers: { 'Authorization': `Bearer ${token}` }, signal: controller.signal
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (!alive) return;

        if (data.success) {
          const normalizedPages = normalizePages(data.pages || []);
          setPages(normalizedPages);
          if (normalizedPages.length > 0) {
            setCurrentPage(normalizedPages[0]);
          }
        } else {
          toast.error(data.message || 'Erreur de chargement');
          navigate('/etudiant/cours/marche');
        }
      } catch (error) {
        if (!alive || error.name === 'AbortError') return;
        console.error('❌ Erreur:', error);
        toast.error('Erreur de chargement: ' + error.message);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchPages();
    return () => { alive = false; controller.abort(); };
  }, [idCours, API_BASE, navigate]);

  // ✅ Rendu des médias avec support YouTube
  const renderMedia = (medias) => {
    if (!medias || medias.length === 0) {
      return null;
    }

    return (
      <div className="mt-8">
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xl">📁</span>
          <h3 className="text-lg font-semibold text-gray-700">Fichiers joints</h3>
          <span className="ml-auto text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full">
            {medias.length} fichier{medias.length > 1 ? 's' : ''}
          </span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {medias.map(media => {
            // ✅ Vérifier si c'est une URL YouTube
            const isYoutube = media.type_media === 'youtube' || isYouTubeUrl(media.url_fichier);
            const videoId = isYoutube ? extractYouTubeId(media.url_fichier) : null;
            
            return (
              <div key={media.id_media} className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden border border-gray-100 hover:border-blue-200">
                
                {/* ✅ Image */}
                {media.type_media === 'image' && (
                  <div className="relative cursor-pointer overflow-hidden" onClick={() => window.open(media.url_fichier)}>
                    <img 
                      src={media.url_fichier} 
                      alt={media.titre || 'Image'} 
                      className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        console.error('❌ Erreur chargement image:', media.url_fichier);
                        e.target.style.display = 'none';
                      }}
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
                      <span className="opacity-0 group-hover:opacity-100 text-white text-sm bg-black/60 px-4 py-2 rounded-full backdrop-blur-sm transition-all duration-300">
                        🔍 Agrandir
                      </span>
                    </div>
                  </div>
                )}

                {/* ✅ Vidéo uploadée */}
                {media.type_media === 'video' && (
                  <div className="relative bg-black/5">
                    <video 
                      src={media.url_fichier} 
                      controls 
                      className="w-full rounded-t-xl"
                      poster={media.thumbnail || undefined}
                    />
                  </div>
                )}

                {/* ✅ Vidéo YouTube */}
                {isYoutube && videoId && (
                  <div className="relative w-full bg-black" style={{ paddingBottom: '56.25%', height: 0 }}>
                    <iframe
                      src={`https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1&autoplay=0`}
                      title={media.titre || 'Vidéo YouTube'}
                      className="absolute top-0 left-0 w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                    />
                    <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded backdrop-blur-sm">
                      YouTube
                    </div>
                  </div>
                )}

                {/* ✅ PDF */}
                {media.type_media === 'pdf' && (
                  <a 
                    href={media.url_fichier} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex items-center gap-4 p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="w-12 h-12 bg-red-50 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
                      📄
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-800 truncate">{media.titre || 'Document PDF'}</p>
                      <p className="text-xs text-gray-400">PDF • Cliquez pour ouvrir</p>
                    </div>
                    <span className="text-gray-300 group-hover:text-blue-500 transition-colors">→</span>
                  </a>
                )}

              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-600 font-medium">Chargement du cours...</p>
          <p className="text-sm text-gray-400">Préparation des pages</p>
        </div>
      </div>
    );
  }

  if (!currentPage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-6xl mb-4">📭</div>
          <h3 className="text-xl font-bold text-gray-800">Aucune page disponible</h3>
          <p className="text-gray-500 mt-2 text-sm">Ce cours ne contient pas encore de pages.</p>
          <button 
            onClick={() => navigate('/etudiant/cours/marche')} 
            className="mt-6 bg-blue-600 text-white px-6 py-2.5 rounded-xl hover:bg-blue-700 transition-all shadow-lg shadow-blue-500/25 font-medium"
          >
            Retour au marché
          </button>
        </div>
      </div>
    );
  }

  return (
    <div ref={readerRef} className={'course-reader' + (wideReading ? ' is-wide' : '')}>
      {/* Header moderne */}
      <div ref={toolbarRef} className="course-reader-toolbar">
        <div className="course-reader-toolbar-inner">
          <div className="course-reader-toolbar-row">
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)} 
              className="course-reader-toolbar-button"
              aria-label={sidebarOpen ? 'Masquer le sommaire' : 'Afficher le sommaire'}
              aria-expanded={sidebarOpen}
              aria-controls="course-reader-toc"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span>Sommaire</span>
            </button>
            
            <button 
              onClick={() => navigate('/etudiant/cours/mes-achats')} 
              className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <span>←</span>
              <span className="hidden sm:inline">Mes cours</span>
            </button>
            
            <div className="flex-1 flex items-center justify-center gap-2 min-w-0">
              <span className="text-sm sm:text-base font-semibold text-gray-800 truncate">
                {currentPage.cours_titre}
              </span>
              <span className="hidden sm:inline-block text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                {pages.length} pages
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setWideReading(value => !value)} aria-pressed={wideReading} className="course-reader-wide-toggle" title="Masquer le menu général pour agrandir la lecture. Échap pour quitter.">
                {wideReading ? 'Quitter la lecture élargie' : 'Lecture élargie'}
              </button>
              <span className="text-xs text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full hidden sm:inline-block">
                {pages.findIndex(p => p.id_page === currentPage.id_page) + 1}/{pages.length}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className={'course-reader-layout' + (sidebarOpen && !compact ? ' has-toc' : '')}>
        {/* Sidebar - Sommaire */}
        {sidebarOpen && <aside ref={drawerRef} id="course-reader-toc" className="course-reader-toc" role={compact ? 'dialog' : undefined} aria-modal={compact ? true : undefined} aria-labelledby="course-reader-toc-title">
          <div className="course-reader-toc-header">
            <h2 id="course-reader-toc-title" className="font-semibold text-gray-800 flex items-center gap-2">
              <span>📖</span> Sommaire
            </h2>
            <button 
              onClick={() => setSidebarOpen(false)} 
              ref={closeRef}
              className="course-reader-toc-close"
              aria-label="Fermer le sommaire"
            >
              ✕
            </button>
          </div>
          <label className="course-reader-search">Rechercher un titre<input type="search" value={tocSearch} onChange={event => setTocSearch(event.target.value)} placeholder="Chapitre ou section…" /></label>
          <nav ref={tocRef} className="course-reader-toc-pages" aria-label="Pages et titres du cours">
            {visiblePages.length === 0 && <p className="course-reader-no-results">Aucun titre ne correspond à votre recherche.</p>}
            {visiblePages.map(page => {
              const index = pages.indexOf(page);
              const isActive = currentPage?.id_page === page.id_page;
              return (
                <div key={page.id_page}>
                <button
                  onClick={() => selectPage(page)}
                  aria-current={isActive ? 'page' : undefined}
                  className={'course-reader-page' + (isActive ? ' is-current' : '')}
                >
                  <span className={`
                    flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium
                    ${isActive ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600 group-hover:bg-gray-300'}
                  `}>
                    {index + 1}
                  </span>
                  <span className="course-reader-page-title">
                    {page.titre}
                  </span>
                  {isActive && (
                    <span className="flex-shrink-0 w-1.5 h-1.5 bg-blue-600 rounded-full"></span>
                  )}
                </button>
                {isActive && documentContent.headings.length > 0 && <ol className="course-reader-sections">
                  {documentContent.headings.filter(heading => !search || String(page.titre || '').toLocaleLowerCase('fr').includes(search) || heading.label.toLocaleLowerCase('fr').includes(search)).map(heading => <li key={heading.id}>
                    <button type="button" data-section-target={heading.id} aria-current={activeHeading === heading.id ? 'location' : undefined} onClick={() => goToHeading(heading.id)} className={'course-reader-section level-' + heading.level + (activeHeading === heading.id ? ' is-current' : '')}>{heading.label}</button>
                  </li>)}
                </ol>}
                </div>
              );
            })}
          </nav>
          <p className="course-reader-toc-footer">{pages.length} page{pages.length > 1 ? 's' : ''} · Cliquez sur un titre pour le rejoindre</p>
        </aside>}

        {sidebarOpen && compact && (
          <button type="button" tabIndex={-1} aria-label="Fermer le sommaire" className="course-reader-scrim" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Contenu principal */}
        <section className="course-reader-main" aria-label="Lecture du cours">
          <article className="course-reader-paper">
            {/* Titre de la page */}
            <header className="course-reader-title">
              <p className="course-reader-eyebrow">PAGE {currentIndex + 1} SUR {pages.length}</p>
              <h1 ref={titleRef} tabIndex={-1} className="text-2xl sm:text-3xl font-bold text-gray-800 leading-tight">
                {currentPage.titre}
              </h1>
              <div className="course-reader-page-meta">
                <span>📄 Page {pages.findIndex(p => p.id_page === currentPage.id_page) + 1}</span>
                <span className="w-px h-4 bg-gray-300"></span>
                <span>📚 {currentPage.cours_titre}</span>
              </div>
            </header>

            {/* Contenu */}
            <div 
              ref={contentRef}
              className="course-reader-content"
              dangerouslySetInnerHTML={{ 
                __html: documentContent.html
              }} 
            />

            {/* Médias */}
            {renderMedia(currentPage.medias)}

            {/* Navigation */}
            <nav className="course-reader-pagination" aria-label="Navigation entre les pages">
              <button
                onClick={() => {
                  const idx = pages.findIndex(p => p.id_page === currentPage.id_page);
                  if (idx > 0) selectPage(pages[idx - 1]);
                }}
                disabled={pages.findIndex(p => p.id_page === currentPage.id_page) === 0}
                className="w-full sm:w-auto px-6 py-2.5 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 disabled:opacity-40 disabled:cursor-not-allowed transition-all flex items-center gap-2 justify-center text-sm font-medium text-gray-700"
              >
                <span>←</span> Page précédente
              </button>
              
              <div className="text-xs text-gray-400 bg-gray-100 px-4 py-1.5 rounded-full">
                {pages.findIndex(p => p.id_page === currentPage.id_page) + 1} / {pages.length}
              </div>
              
              <button
                onClick={() => {
                  const idx = pages.findIndex(p => p.id_page === currentPage.id_page);
                  if (idx < pages.length - 1) selectPage(pages[idx + 1]);
                }}
                disabled={pages.findIndex(p => p.id_page === currentPage.id_page) === pages.length - 1}
                className="w-full sm:w-auto px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-lg shadow-blue-500/25 flex items-center gap-2 justify-center text-sm font-medium"
              >
                Page suivante <span>→</span>
              </button>
            </nav>

            {/* Progression */}
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                <div 
                  className="bg-gradient-to-r from-blue-600 to-indigo-600 h-1.5 rounded-full transition-all duration-500"
                  style={{ 
                    width: `${((pages.findIndex(p => p.id_page === currentPage.id_page) + 1) / pages.length) * 100}%` 
                  }}
                />
              </div>
              <p className="text-xs text-gray-400 text-center mt-2">
                {Math.round(((pages.findIndex(p => p.id_page === currentPage.id_page) + 1) / pages.length) * 100)}% du cours
              </p>
            </div>
          </article>
        </section>
      </div>
      {compact && !sidebarOpen && <button type="button" className="course-reader-floating-toc" onClick={() => setSidebarOpen(true)} aria-controls="course-reader-toc" aria-expanded={false}>☰ Sommaire</button>}
    </div>
  );
}
